<template>
  <div class="settings-block apikey-wr">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{ t('API Access Keys') }}
      </div>
      <div class="settings-block__title-buttons">
        <ui-button
          :text="t('Create API Key')"
          icon="fas fa-plus"
          button-style="outline-accent"
          @click="openCreateApiKey"
        />
      </div>
    </div>

    <p
      class="apikey-wr__intro"
      v-html="
        t(
          'Use access keys to make secure REST or HTTP Query protocol requests to BeMob service APIs.<br>For your protection, you should never share your secret keys with anyone.',
        )
      "
    />

    <div class="settings-layout__table-wrapper">
      <ui-table
        class="apikey-wr__table"
        :headers="tableHeaders"
        :items="rows"
        :use-search="false"
        :loading="keysLoading"
        :show-pagination="rows.length > rowsOnPage"
        :rows-per-page-count="rowsOnPage"
        :virtualize="false"
      >
        <template #items="{ item }">
          <div class="table-cell apikey-wr__access-cell">
            <v-tooltip location="top">
              <template #activator="{ props: activatorProps }">
                <span v-bind="activatorProps" class="apikey-wr__access-text">{{
                  String(item.accessKey ?? '')
                }}</span>
              </template>
              <span>{{ item.accessKey }}</span>
            </v-tooltip>
          </div>
          <div class="table-cell">{{ item.created }}</div>
          <div class="table-cell apikey-wr__activity-cell">
            <template v-if="activityParts(item)">
              <div>{{ activityParts(item)?.remoteAddr }}</div>
              <div>({{ activityParts(item)?.relative }})</div>
            </template>
          </div>
          <div class="table-cell">{{ accessLevelLabel(item) }}</div>
          <div class="table-cell apikey-wr__url-access-cell">
            <ui-icon
              size="14"
              :color="item.allowUrlAccess ? $colors.green : $colors.red"
              :icon="item.allowUrlAccess ? 'fas fa-check-circle' : 'fas fa-times-circle'"
            />
          </div>
          <div class="table-cell apikey-wr__workspaces-cell">
            <span v-if="!item.workspaceIds || !item.workspaceIds.length">
              {{ t('All Workspaces') }}
            </span>
            <div v-else class="onePlus-wrp">
              <div class="onePlus-text">
                {{ store.allWorkspacesObject[item.workspaceIds[0]]?.name }}
              </div>
              <v-tooltip
                v-if="item.workspaceIds.length > 1"
                :color="$colors.white"
                content-class="onePlus-tooltip"
                class="ml-8"
                location="top"
              >
                <template #activator="{ props: activatorProps }">
                  <div class="onePlus-text onePlus-text--count ml-8" v-bind="activatorProps">
                    {{ `+${item.workspaceIds.length - 1}` }}
                  </div>
                </template>
                <div
                  v-for="workspaceId in item.workspaceIds.slice(1)"
                  :key="workspaceId"
                  class="onePlus-text"
                >
                  {{ store.allWorkspacesObject[workspaceId]?.name }}
                </div>
              </v-tooltip>
            </div>
          </div>
          <div class="table-cell apikey-wr__actions-cell">
            <div class="apikey-wr__actions">
              <a
                href="#"
                class="apikey-wr__action"
                :class="{ 'apikey-wr__action--disabled': !item.allowUrlAccess }"
                @click.prevent="openEditApiKey(item)"
              >
                <ui-icon
                  size="12"
                  :color="item.allowUrlAccess ? $colors.green : $colors.gray40"
                  icon="fas fa-pen"
                  class="apikey-wr__action-icon"
                />
                {{ t('Edit') }}
              </a>
              <a href="#" class="apikey-wr__action apikey-wr__delete" @click.prevent="confirmDeleteKey(item)">
                <ui-icon
                  size="12"
                  :color="$colors.red"
                  icon="fas fa-trash-alt"
                  class="apikey-wr__action-icon"
                />
                {{ t('Delete') }}
              </a>
            </div>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import moment from 'moment-timezone';
import { deleteUserApiKeyApiRequest } from '@/services/api/user-api';
import { useI18n, useColors } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';

const props = withDefaults(
  defineProps<{
    dataApiKeys?: unknown;
    reloadApiKeys: () => Promise<void>;
    format?: string;
  }>(),
  {
    format: 'DD MMM YYYY HH:mm',
  },
);

const { t } = useI18n();
const $colors = useColors();
const store = useStore();

const keysLoading = ref(true);
const rows = ref<Record<string, unknown>[]>([]);
const rowsOnPage = 10;

const tableHeaders = computed(() => [
  { text: t('Access Key'), value: 'accessKey', sortable: true },
  { text: t('Created At'), value: 'createdSortMs', sortable: true },
  { text: t('Last Activity'), value: 'lastActivitySortMs', sortable: true },
  { text: t('Access Level'), value: 'accessLevel', sortable: true },
  { text: t('URLs Access'), value: 'allowUrlAccess', sortable: false },
  { text: t('Workspaces'), value: 'workspaceIds', sortable: false },
  { text: '', value: 'actions' },
]);

const transformApiKeys = (data: unknown) => {
  const list = Array.isArray(data) ? data : [];
  const tz = store.user?.defaultTimezone;
  return list.map((item: Record<string, unknown>) => {
    const created = moment.utc(String(item.created));
    const lastActivityRaw = item.latestActivity;
    const lastActivityMoment = moment.utc(lastActivityRaw as moment.MomentInput);
    return {
      ...item,
      allowUrlAccess: Boolean(item.allowUrlAccess ?? true),
      created: created.isValid()
        ? tz
          ? created.tz(tz).format(props.format)
          : created.format(props.format)
        : '',
      createdSortMs: created.isValid() ? created.valueOf() : 0,
      lastActivitySortMs: lastActivityMoment.isValid() ? lastActivityMoment.valueOf() : 0,
      latestActivity: {
        remoteAddr: item.remoteAddr,
        latestActivity: lastActivityRaw,
      },
    };
  });
};

watch(
  () => props.dataApiKeys,
  data => {
    if (data == null) return;
    rows.value = transformApiKeys(data);
    keysLoading.value = false;
  },
  { immediate: true },
);

const accessLevelLabel = (item: Record<string, unknown>) =>
  item.accessLevel === 'readOnly' ? t('Read Only') : t('Full Access');

const activityParts = (item: Record<string, unknown>) => {
  const la = item.latestActivity as
    | { remoteAddr?: unknown; latestActivity?: unknown }
    | undefined;
  if (!la) return null;
  const remoteAddr = la.remoteAddr;
  const ts = la.latestActivity;
  if (!remoteAddr && !ts) return null;
  const m = moment.utc(ts as moment.MomentInput);
  return {
    remoteAddr: String(remoteAddr ?? ''),
    relative: m.isValid() ? m.fromNow() : '',
  };
};

const confirmDeleteKey = (item: Record<string, unknown>) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: async () => {
        keysLoading.value = true;
        try {
          await deleteUserApiKeyApiRequest(String(item.accessKey));
          await props.reloadApiKeys();
          $emitter.emit('toast-success', t('API key deleted.'));
        } finally {
          keysLoading.value = false;
        }
      },
    },
  });
};

const openCreateApiKey = () => {
  $emitter.emit('open-dialog', {
    name: 'CreateApiKeys',
    data: { reloadApiKeys: props.reloadApiKeys },
  });
};

const openEditApiKey = (item: Record<string, unknown>) => {
  if (!item.allowUrlAccess) return;
  $emitter.emit('open-dialog', {
    name: 'CreateApiKeys',
    data: { reloadApiKeys: props.reloadApiKeys, apiKey: item },
  });
};
</script>

<style lang="scss" scoped>

.apikey-wr__intro {
  color: var(--color-graphite);
  font-size: 13px;
  line-height: 1.69;
  margin: 0 0 16px;
}

.apikey-wr__table {
  margin-top: 16px;

  :deep(.ui-table__inner) {
    table-layout: fixed;
    width: 100%;
    max-width: 100%;
  }

  :deep(.table-header .table-cell),
  :deep(.table-row .table-cell) {
    padding-right: 5px !important;
  }

  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-row .table-cell:nth-child(1)) {
    width: 24% !important;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-row .table-cell:nth-child(2)),
  :deep(.table-row .table-cell:nth-child(3)) {
    width: 16% !important;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(4)),
  :deep(.table-row .table-cell:nth-child(4)) {
    width: 14% !important;
    min-width: 0;
    white-space: normal;
  }

  :deep(.table-header .table-cell:nth-child(5)),
  :deep(.table-row .table-cell:nth-child(5)) {
    width: 10% !important;
    min-width: 0;
    justify-content: center;
    text-align: center;
    white-space: normal;
  }

  :deep(.table-header .table-cell:nth-child(6)),
  :deep(.table-row .table-cell:nth-child(6)) {
    width: 16% !important;
    min-width: 0;
    white-space: normal;
  }

  :deep(.table-header .table-cell:nth-child(7)),
  :deep(.table-row .table-cell:nth-child(7)) {
    flex: 0 0 auto;
    width: 130px !important;
    min-width: 0;
    max-width: none;
    justify-content: flex-end;
    text-align: right;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
  }

  :deep(.table-header .table-cell:nth-child(7):lang(ru)),
  :deep(.table-row .table-cell:nth-child(7):lang(ru)) {
    width: 150px !important;
  }

  :deep(.table-row .table-cell:nth-child(3)) {
    line-height: 13px;
    padding-top: 4px;
    padding-bottom: 4px;
    white-space: normal;
  }
}

.apikey-wr__access-cell {
  vertical-align: middle;

  :deep(.v-tooltip) {
    display: block;
    min-width: 0;
    max-width: 100%;
  }
}

.apikey-wr__access-text {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apikey-wr__url-access-cell {
  justify-content: center;
}

.apikey-wr__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.apikey-wr__action {
  display: flex;
  align-items: center;
  margin-left: 12px;
  color: var(--color-green);
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;

  &--disabled {
    color: var(--color-gray40);
    cursor: default;
    pointer-events: none;
  }
}

.apikey-wr__delete {
  color: var(--color-red);
}

.apikey-wr__action-icon {
  margin-right: 4px;
  vertical-align: baseline;
}
</style>
